import React, { useEffect, useRef, useState } from "react";
import { Link, useHref, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { formatDistanceToNow, formatDistance, format, isValid } from "date-fns";
import cn from "classnames";

import { communityColor } from "../../../../helpers/communityColor";
import { parseApiDate } from "../../../../helpers";
import { trackDataLayer } from "../../../../helpers/track";
import { eventNameDataLayer } from "../../../../constants/track";
import { Button } from "../../../common/button";
import { Notification, NotificationError } from "../../../common/notification";
import MessageModal from "../../../publicOrganizations/messageModal";
import { ReactComponent as HeartIcon } from "../../../../media/icons/heart.svg";
import { ReactComponent as ShareIcon } from "../../../../media/icons/share.svg";
import { ReactComponent as CalendarIcon } from "../../../../media/icons/calendarNew.svg";
import { ReactComponent as LocationIcon } from "../../../../media/icons/linear-location.svg";
import { whenFor } from "./whenFor";
import styles from "./feedCard.module.css";

// hlm_listing_jobs lookups (GROWTH-7129): same ids as publicListing/jobContent, which keeps its
// own local copy of the English labels — these go through i18n instead.
const JOB_TYPE_LABEL_KEYS = {
  1: "dashboard_feed_job_type_full_time",
  2: "dashboard_feed_job_type_part_time",
  3: "dashboard_feed_job_type_contract",
  4: "dashboard_feed_job_type_temporary",
  5: "dashboard_feed_job_type_volunteer",
  6: "dashboard_feed_job_type_internship"
};
// Exported: the header search (GROWTH-7577) words a job's type with these keys.
export { JOB_TYPE_LABEL_KEYS };
const WORKPLACE_TYPE_LABEL_KEYS = {
  1: "dashboard_feed_workplace_in_person",
  2: "dashboard_feed_workplace_hybrid",
  3: "dashboard_feed_workplace_remote"
};

// Each card type's public dashboard page (routes.js). /public-listing renders the event/job
// template, so a profile or organization sent there shows an empty event page.
const PUBLIC_PATHS = {
  profile: "/public-profile",
  organization: "/public-organizations",
  event: "/public-listing",
  job: "/public-listing"
};

// A click on or inside one of these is that control's own; the card's body click (GROWTH-7576) leaves it alone.
const CARD_CLICK_IGNORED = "a, button, input, select, textarea, label";

// How long the "Link copied" notice stays up after Share.
const SHARE_STATUS_MS = 2500;

const initials = name =>
  (name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(word => word[0])
    .join("")
    .toUpperCase();
// Exported: the upcoming-events panel's image card (GROWTH-7573) uses the same initials fallback.
export { initials };

const formatLocation = location => {
  if (!location) return null;
  return [location.city, location.state || location.country].filter(Boolean).join(", ") || null;
};
// Exported: upcomingEventsStrip (GROWTH-7131) reuses this exact formatting, not a copy.
export { formatLocation };

const formatLicensed = (licensed, t) => {
  if (!licensed) return null;
  const duration = licensed.through
    ? formatDistance(new Date(licensed.since), new Date(licensed.through))
    : formatDistanceToNow(new Date(licensed.since));
  return t("dashboard_feed_licensed", { duration });
};

const formatPrice = (price, t) => {
  if (!price) return null;
  if (Number(price.min) === 0 && Number(price.max) === 0) return t("dashboard_feed_price_free");
  const currency = price.currency || "USD";
  const fmt = amount => new Intl.NumberFormat(undefined, { style: "currency", currency }).format(amount);
  return price.min === price.max ? fmt(price.min) : `${fmt(price.min)}–${fmt(price.max)}`;
};
// Exported: upcomingEventsStrip (GROWTH-7131) reuses this exact formatting, not a copy.
export { formatPrice };

// details.starts_at is a MySQL DATETIME ("Y-m-d H:i:s"); parseApiDate swaps the space for "T",
// which older Safari needs. null when absent or unparseable, so format() never sees an Invalid Date.
const eventStart = details => {
  const startsAt = parseApiDate(details.starts_at);
  return startsAt && isValid(startsAt) ? startsAt : null;
};
// Exported: upcomingEventsStrip (GROWTH-7131) reuses this exact parsing, not a copy.
export { eventStart };

/**
 * One card of the My Feed v2 stream (GROWTH-7129): community attribution, a 150×150 media
 * column (CardMedia, GROWTH-7573), title linking to the listing's public page, a type-specific
 * meta row, quiet save/share icon buttons, and a per-type CTA column pinned right, wrapping under
 * the content on a narrow card (GROWTH-7132; layout GROWTH-7573). Renders items from
 * GET /api-v2/feed and GET /api-v2/feed/saved (FeedCardPresenter::present() on the backend)
 * unchanged.
 *
 * Share opens the browser's share sheet when it has one, else copies the public page's URL.
 *
 * The whole card opens the listing's page (GROWTH-7576): a pointer click anywhere on it that isn't on one of its own
 * links or buttons, and isn't the end of a text selection. Ctrl/⌘/Shift-click and a middle click open it in a new tab,
 * as on a link. Keyboard users reach the page through the title link; the card itself is not a tab stop. The Message
 * modal and the notices render beside the card, not inside it, so a click in them never reaches the card's handler.
 *
 * CTA row: profile gets a primary Message button (the same "message an attorney" modal/endpoint
 * the public profile page uses) plus a secondary View profile link; organization gets a primary
 * Visit link to the listing. Job gets a primary Apply link to `details.apply_url` and event a
 * primary Learn More link to `details.website` — the pages the directory's own "Apply now" and
 * "Buy ticket" open — both in a new tab, plus a secondary Details link; with no URL, Details alone.
 *
 * @param {Object} props
 * @param {Object} props.item one FeedItem
 * @param {Function} props.onToggleBookmark (id, nextBookmarked) => Promise<boolean|undefined>
 * @param {Function} props.onSendMessage (data) => Promise<boolean> — GROWTH-7132 Message CTA
 * @param {Object} props.senderProfile the signed-in member's own profile (state.account.profile),
 *   used to prefill the Message form's name/phone, same as the public profile page's MessageModal
 * @param {Function} [props.onSelectCommunity] (id: number) => void — the My Feed sidebar's scope
 *   (GROWTH-7130). Omitted on the Saved page: community scoping doesn't apply there.
 * @param {Object} [props.linkState] the router state the card's links to the listing page carry — the feed's filter,
 *   for the page's "My Feed" breadcrumb (GROWTH-7576, helpers/feedReturn.js). Omitted on the Saved page.
 * @param {boolean} [props.showReason=true] the attribution line's time slot (whenFor, GROWTH-7578) shows
 *   why the item is in the feed from 7 days on; false on the Saved page, which has no such reason
 */
const FeedCard = ({ item, onToggleBookmark, onSendMessage, senderProfile, onSelectCommunity, linkState, showReason = true }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [bookmarked, setBookmarked] = useState(item.bookmarked === true);
  const [busy, setBusy] = useState(false);
  const [shareStatusKey, setShareStatusKey] = useState(null);
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [messageError, setMessageError] = useState(null);
  const [messageSent, setMessageSent] = useState(false);
  const shareStatusTimer = useRef(null);
  const details = item.details || {};
  const when = whenFor(item, t, { now: new Date(), city: senderProfile?.city, state: senderProfile?.state, showReason });

  useEffect(() => () => clearTimeout(shareStatusTimer.current), []);

  const handleToggleBookmark = async () => {
    if (busy) return;
    const next = !bookmarked;
    setBusy(true);
    setBookmarked(next);
    const ok = await onToggleBookmark(item.id, next);
    if (ok !== true) setBookmarked(!next);
    setBusy(false);
  };

  const showShareStatus = key => {
    clearTimeout(shareStatusTimer.current);
    setShareStatusKey(key);
    shareStatusTimer.current = setTimeout(() => setShareStatusKey(null), SHARE_STATUS_MS);
  };

  const href = `${PUBLIC_PATHS[item.type] || "/public-listing"}/${item.id}`;
  // The same page as a URL the browser can open in another tab (router basename included).
  const tabHref = useHref(href);

  const handleShare = async () => {
    const url = `${window.location.origin}${href}`;

    if (navigator.share) {
      try {
        await navigator.share({ title: item.name, url });
        return;
      } catch (error) {
        // The member closed the share sheet — nothing to report. Any other failure falls
        // through to copying the link.
        if (error?.name === "AbortError") return;
      }
    }

    try {
      await navigator.clipboard.writeText(url);
      showShareStatus("dashboard_feed_share_copied");
    } catch {
      showShareStatus("dashboard_feed_share_failed");
    }
  };

  const typeLabelKey = {
    profile: "dashboard_feed_type_profile",
    organization: "dashboard_feed_type_organization",
    event: "dashboard_feed_type_event",
    job: "dashboard_feed_type_job"
  }[item.type];

  const trackCta = cta => trackDataLayer(eventNameDataLayer.feedCardClick, { type: item.type, cta });

  const handleMessageCtaClick = () => {
    trackCta("message");
    setMessageModalOpen(true);
  };

  const closeMessageModal = () => {
    setMessageModalOpen(false);
    setMessageError(null);
  };

  const handleMessageSubmit = async values => {
    setSendingMessage(true);
    setMessageError(null);
    const result = await onSendMessage({ listingId: item.id, dirId: item.dir_id, ...values });
    setSendingMessage(false);
    if (result?.ok) {
      setMessageModalOpen(false);
      setMessageSent(true);
    } else {
      // The endpoint's own reason ("not accepting direct messages", "Phone number is required"…)
      // tells the member what to change; the generic text is only for when there is none.
      setMessageError(result?.message || t("dashboard_feed_message_failed"));
    }
  };

  const handleLinkCtaClick = cta => () => trackCta(cta);

  const handleCardClick = event => {
    if (event.target.closest(CARD_CLICK_IGNORED)) return;
    // Selecting the description's text ends in a click; it isn't a request to open the page.
    if (window.getSelection?.().toString()) return;
    trackCta("card");
    // Ctrl/⌘/Shift-click and a middle click open a new tab, as they do on the title link. Router state can't travel to
    // another tab, so that page's "My Feed" opens the feed as it opens by default.
    if (event.button === 1 || event.metaKey || event.ctrlKey || event.shiftKey) {
      window.open(tabHref, "_blank", "noopener");
      return;
    }
    navigate(href, { state: linkState });
  };

  // The browser fires no `click` for the middle button, only `auxclick`; the right button keeps its context menu.
  const handleCardAuxClick = event => {
    if (event.button === 1) handleCardClick(event);
  };

  return (
    <>
      {/* Pointer-only on purpose: the title link is the keyboard and screen-reader route to the same page. */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
      <article className={styles.card} onClick={handleCardClick} onAuxClick={handleCardAuxClick}>
        <div className={styles.attribution}>
          <span className={styles.communityDot} style={{ backgroundColor: communityColor(item.community_id) }} />
          {item.community_name && onSelectCommunity ? (
            <button type="button" className={styles.communityNameButton} onClick={() => onSelectCommunity(item.community_id)}>
              {item.community_name}
            </button>
          ) : item.community_name ? (
            <span className={styles.communityName}>{item.community_name}</span>
          ) : null}
          {item.community_name ? <span aria-hidden="true">·</span> : null}
          <span>{t(typeLabelKey)}</span>
          {when ? (
            <>
              <span aria-hidden="true">·</span>
              <span className={styles.when}>{when}</span>
            </>
          ) : null}
          {item.is_new ? <span className={styles.newTag}>{t("dashboard_feed_new_tag")}</span> : null}
        </div>

        <div className={styles.actions}>
          {/* Plain buttons, not common/button: its global .button padding (15px) leaves a 32px
            icon button 2px for the icon, and its clearStyles variant removes the focus outline. */}
          <button
            type="button"
            className={cn(styles.iconButton, { [styles.iconButtonActive]: bookmarked })}
            aria-label={t(bookmarked ? "dashboard_feed_unsave_aria" : "dashboard_feed_save_aria")}
            onClick={handleToggleBookmark}
            disabled={busy}
          >
            <HeartIcon aria-hidden="true" />
          </button>
          <button type="button" className={styles.iconButton} aria-label={t("dashboard_feed_share_aria")} onClick={handleShare}>
            <ShareIcon aria-hidden="true" />
          </button>
          <span className={styles.shareStatus} role="status">
            {shareStatusKey ? t(shareStatusKey) : ""}
          </span>
        </div>

        <div className={styles.body}>
          <CardMedia item={item} details={details} />

          <div className={styles.content}>
            <div className={styles.titleRow}>
              <Link to={href} state={linkState} className={styles.title}>
                {item.name}
              </Link>
              {item.is_premium ? <span className={styles.sponsorTag}>{t("dashboard_feed_sponsor_tag")}</span> : null}
            </div>

            {renderMeta(item, details, t)}

            {item.type === "profile" ? (
              details.practice_areas?.length ? (
                <div className={styles.chips}>
                  {details.practice_areas.map(area => (
                    <span key={area} className={styles.chip}>
                      {area}
                    </span>
                  ))}
                </div>
              ) : null
            ) : item.description ? (
              <p className={styles.description}>{item.description}</p>
            ) : null}
          </div>

          {renderCta(item, href, linkState, t, { onMessageClick: handleMessageCtaClick, onLinkClick: handleLinkCtaClick })}
        </div>
      </article>

      {messageModalOpen ? (
        <MessageModal
          defaultValue={{ fullName: `${senderProfile?.name_f || ""} ${senderProfile?.name_l || ""}`.trim(), phone: senderProfile?.phone }}
          listingName={item.name}
          closeModal={closeMessageModal}
          onSubmit={handleMessageSubmit}
          loading={sendingMessage}
        />
      ) : null}
      {messageError ? <NotificationError text={messageError} time={5000} onClose={() => setMessageError(null)} /> : null}
      {messageSent ? (
        <Notification success time={5000} close={() => setMessageSent(false)}>
          {t("dashboard_feed_message_sent")}
        </Notification>
      ) : null}
    </>
  );
};

/**
 * The card's 150×150 media column (GROWTH-7573). With an image_url: the photo — a circle for a
 * profile, a rounded square for the other types, an event's carrying its month/day badge. Without
 * one, or once the image fails to load (so never a broken-image glyph): initials in a circle for a
 * profile, a date tile for an event (a calendar icon when starts_at is missing), an initials tile for
 * an organization or job.
 *
 * @param {Object} props
 * @param {Object} props.item one FeedItem
 * @param {Object} props.details item.details, never null
 */
const CardMedia = ({ item, details }) => {
  const [imageFailed, setImageFailed] = useState(false);
  const isProfile = item.type === "profile";
  const startsAt = item.type === "event" ? eventStart(details) : null;
  const shapeClass = isProfile ? styles.avatarCircle : styles.avatarSquare;

  if (item.image_url && !imageFailed) {
    return (
      <span className={cn(styles.avatar, shapeClass, styles.avatarPhoto)}>
        <span className={styles.avatarPhotoFallback} aria-hidden="true">
          {initials(item.name)}
        </span>
        <img src={item.image_url} alt="" width="150" height="150" loading="lazy" onError={() => setImageFailed(true)} />
        {startsAt ? (
          <span className={styles.dateBadge}>
            <span className={styles.dateBadgeMonth}>{format(startsAt, "MMM")}</span>
            <span className={styles.dateBadgeDay}>{format(startsAt, "d")}</span>
          </span>
        ) : null}
      </span>
    );
  }

  if (item.type === "event") {
    return (
      <span className={styles.avatarEvent}>
        {startsAt ? (
          <>
            <span className={styles.avatarEventMonth}>{format(startsAt, "MMM")}</span>
            <span className={styles.avatarEventDay}>{format(startsAt, "d")}</span>
          </>
        ) : (
          <CalendarIcon aria-hidden="true" />
        )}
      </span>
    );
  }

  return (
    <span className={cn(styles.avatar, shapeClass, isProfile ? styles.avatarInitials : styles.avatarTile)} aria-hidden="true">
      {initials(item.name)}
    </span>
  );
};

function renderCta(item, href, linkState, t, { onMessageClick, onLinkClick }) {
  if (item.type === "profile") {
    return (
      <div className={styles.ctaRow}>
        <Button className={styles.ctaButton} onClick={onMessageClick}>
          {t("dashboard_feed_cta_message")}
        </Button>
        <Link to={href} state={linkState} className={styles.ctaSecondary} onClick={onLinkClick("view_profile")}>
          {t("dashboard_feed_cta_view_profile")}
        </Link>
      </div>
    );
  }

  if (item.type === "organization") {
    return (
      <div className={styles.ctaRow}>
        <Link to={href} state={linkState} className={cn("button", styles.ctaButton)} onClick={onLinkClick("visit")}>
          {t("dashboard_feed_cta_visit")}
        </Link>
      </div>
    );
  }

  // Job and event: an external primary link when the backend found a usable URL, then Details.
  const external =
    item.type === "job"
      ? { url: item.details?.apply_url, cta: "apply", labelKey: "dashboard_feed_cta_apply" }
      : { url: item.details?.website, cta: "learn_more", labelKey: "dashboard_feed_cta_learn_more" };

  return (
    <div className={styles.ctaRow}>
      {external.url ? (
        <a
          href={external.url}
          target="_blank"
          rel="noopener noreferrer"
          className={cn("button", styles.ctaButton)}
          onClick={onLinkClick(external.cta)}
        >
          {t(external.labelKey)}
        </a>
      ) : null}
      <Link to={href} state={linkState} className={styles.ctaSecondary} onClick={onLinkClick("details")}>
        {t("dashboard_feed_cta_details")}
      </Link>
    </div>
  );
}

function renderMeta(item, details, t) {
  const parts = [];

  if (item.type === "profile" && details.firm) parts.push(details.firm);
  if (item.type === "job" && details.company) parts.push(details.company);
  if (item.type === "event" && details.organizer) parts.push(details.organizer);

  const location = formatLocation(item.location);
  if (location) {
    parts.push(
      <span key="location" className={styles.metaWithIcon}>
        <LocationIcon /> {location}
      </span>
    );
  }

  const startsAt = item.type === "event" ? eventStart(details) : null;
  if (startsAt) {
    parts.push(
      <span key="date" className={styles.metaWithIcon}>
        <CalendarIcon /> {format(startsAt, "MMM d, yyyy")}
      </span>
    );
  }

  if (item.type === "job") {
    const jobTypeLabels = (details.job_type_ids || []).map(id => JOB_TYPE_LABEL_KEYS[id]).filter(Boolean);
    const workplaceLabels = (details.workplace_type_ids || []).map(id => WORKPLACE_TYPE_LABEL_KEYS[id]).filter(Boolean);
    [...jobTypeLabels, ...workplaceLabels].forEach(labelKey => parts.push(t(labelKey)));
    if (details.compensation) parts.push(details.compensation);
  }

  if (item.type === "profile") {
    const licensedText = formatLicensed(details.licensed, t);
    if (licensedText) parts.push(licensedText);
  }

  if (item.type === "event") {
    const priceText = formatPrice(details.price, t);
    if (priceText) {
      parts.push(
        <span key="price" className={styles.metaPrice}>
          {priceText}
        </span>
      );
    }
  }

  if (!parts.length) return null;

  return (
    <div className={styles.meta}>
      {parts.map((part, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <span key={index}>{part}</span>
      ))}
    </div>
  );
}

export default FeedCard;
